/**
 * Message composer with @mention autocomplete.
 * Type @ to trigger — searches staff + entities (customers, VPS).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { sendMessage, listStaff, searchEntities, type EntityMention } from '@/api/team'
import { sendTeamFrame } from '@/hooks/useTeamStream'
import { useTeamStore } from '@/store/team'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

interface Props {
  channelId: string
}

interface MentionOption {
  kind: string
  id: string
  display: string
  subtitle?: string
}

export function MessageInput({ channelId }: Props) {
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [mentions, setMentions] = useState<EntityMention[]>([])
  const ref = useRef<HTMLTextAreaElement>(null)
  const typingRef = useRef(false)
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mention autocomplete state
  const [mentionActive, setMentionActive] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionStart, setMentionStart] = useState(0)
  const [selectedIdx, setSelectedIdx] = useState(0)

  // Staff list (cached)
  const staffQ = useQuery({
    queryKey: ['team-staff-mentions'],
    queryFn: async () => {
      const res = await listStaff()
      return res.staff
    },
    staleTime: 60_000,
  })

  // Entity search (debounced)
  const [entityQuery, setEntityQuery] = useState('')
  const entityQ = useQuery({
    queryKey: ['team-entity-search', entityQuery],
    queryFn: () => searchEntities(entityQuery),
    enabled: entityQuery.length >= 2,
    staleTime: 10_000,
  })

  // Debounce entity search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!mentionActive || mentionQuery.length < 2) {
      setEntityQuery('')
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setEntityQuery(mentionQuery)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [mentionQuery, mentionActive])

  // Build options list
  const options: MentionOption[] = []
  if (mentionActive) {
    const q = mentionQuery.toLowerCase()
    // Staff matches
    for (const s of staffQ.data ?? []) {
      const name = s.display_name || s.email.split('@')[0]
      if (name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q)) {
        options.push({ kind: 'user', id: s.id, display: name, subtitle: s.email })
      }
    }
    // Entity matches
    for (const e of entityQ.data?.results ?? []) {
      options.push({ kind: e.kind, id: e.id, display: e.display, subtitle: e.subtitle || e.kind })
    }
  }

  const handleTyping = useCallback(() => {
    if (!typingRef.current) {
      typingRef.current = true
      sendTeamFrame({ type: 'typing', channel_id: channelId, is_typing: true })
    }
    if (typingTimer.current) clearTimeout(typingTimer.current)
    typingTimer.current = setTimeout(() => {
      typingRef.current = false
      sendTeamFrame({ type: 'typing', channel_id: channelId, is_typing: false })
    }, 3000)
  }, [channelId])

  const insertMention = (opt: MentionOption) => {
    // Replace @query with @[Display](kind:id)
    const before = text.slice(0, mentionStart)
    const after = text.slice(mentionStart + mentionQuery.length + 1) // +1 for the @
    const mention = `@[${opt.display}](${opt.kind}:${opt.id})`
    setText(before + mention + ' ' + after)
    setMentions((prev) => [...prev, { kind: opt.kind, id: opt.id, display: opt.display }])
    setMentionActive(false)
    setMentionQuery('')
    setSelectedIdx(0)
    ref.current?.focus()
  }

  const submit = useCallback(async () => {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    typingRef.current = false
    sendTeamFrame({ type: 'typing', channel_id: channelId, is_typing: false })
    try {
      const msg = await sendMessage(channelId, body, mentions.length > 0 ? mentions : undefined)
      // Add to store immediately so the sender sees it without waiting for WS
      useTeamStore.getState().appendMessage(msg)
      setText('')
      setMentions([])
      ref.current?.focus()
    } catch {
      // toast handled by API client
    } finally {
      setSending(false)
    }
  }, [text, channelId, sending, mentions])

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setText(val)
    handleTyping()

    // Detect @ trigger
    const pos = e.target.selectionStart ?? val.length
    const beforeCursor = val.slice(0, pos)
    const atIdx = beforeCursor.lastIndexOf('@')

    if (atIdx >= 0) {
      const charBefore = atIdx > 0 ? beforeCursor[atIdx - 1] : ' '
      // Only trigger if @ is at start or preceded by whitespace
      if (atIdx === 0 || /\s/.test(charBefore)) {
        const query = beforeCursor.slice(atIdx + 1)
        // Cancel if query contains whitespace (user moved past the mention)
        if (!/\s/.test(query) && query.length <= 30) {
          setMentionActive(true)
          setMentionQuery(query)
          setMentionStart(atIdx)
          setSelectedIdx(0)
          return
        }
      }
    }
    setMentionActive(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (mentionActive && options.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx((i) => Math.min(i + 1, options.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(options[selectedIdx])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionActive(false)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  // Attach entity picker state
  const [attachOpen, setAttachOpen] = useState(false)
  const [attachQuery, setAttachQuery] = useState('')
  const [attachResults, setAttachResults] = useState<MentionOption[]>([])
  const [attachLoading, setAttachLoading] = useState(false)
  const attachDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const searchAttach = (q: string) => {
    setAttachQuery(q)
    if (q.length < 2) { setAttachResults([]); return }
    if (attachDebounce.current) clearTimeout(attachDebounce.current)
    attachDebounce.current = setTimeout(async () => {
      setAttachLoading(true)
      try {
        const res = await searchEntities(q)
        setAttachResults(res.results.map(r => ({
          kind: r.kind, id: r.id, display: r.display, subtitle: r.subtitle,
        })))
      } catch { setAttachResults([]) }
      setAttachLoading(false)
    }, 250)
  }

  const attachEntity = (opt: MentionOption) => {
    const mention = `@[${opt.display}](${opt.kind}:${opt.id})`
    setText(prev => prev ? `${prev} ${mention} ` : `${mention} `)
    setMentions(prev => [...prev, { kind: opt.kind, id: opt.id, display: opt.display }])
    setAttachOpen(false)
    setAttachQuery('')
    setAttachResults([])
    ref.current?.focus()
  }

  const iconForKind = (kind: string) => {
    switch (kind) {
      case 'user': return 'user'
      case 'customer': return 'user-tag'
      case 'vps': return 'server'
      case 'payment': return 'credit-card'
      case 'subscription': return 'layer-group'
      default: return 'circle'
    }
  }

  const kindColor = (kind: string) => {
    switch (kind) {
      case 'customer': return 'text-brand-500'
      case 'vps': return 'text-success'
      case 'payment': return 'text-warn'
      default: return 'text-ink-400'
    }
  }

  return (
    <div className="border-t border-hairline p-2 relative">
      {/* Mention autocomplete popup */}
      {mentionActive && options.length > 0 && (
        <div className="absolute bottom-full left-2 right-2 mb-1 bg-surface border border-hairline rounded-lg shadow-xl max-h-48 overflow-y-auto z-10" style={{ scrollbarWidth: 'none' }}>
          {options.slice(0, 8).map((opt, i) => (
            <button
              key={`${opt.kind}:${opt.id}`}
              onMouseDown={(e) => {
                e.preventDefault()
                insertMention(opt)
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-1.5 text-left cursor-pointer',
                i === selectedIdx ? 'bg-primary/10' : 'hover:bg-surface-alt',
              )}
            >
              <Icon name={iconForKind(opt.kind)} variant="solid" className={cn('text-[10px] w-4 text-center', kindColor(opt.kind))} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-ink-900 truncate">{opt.display}</div>
                {opt.subtitle && (
                  <div className="text-[10px] text-ink-400 truncate">{opt.subtitle}</div>
                )}
              </div>
              <span className="text-[9px] text-ink-400 uppercase">{opt.kind}</span>
            </button>
          ))}
        </div>
      )}

      {/* Entity attach picker */}
      {attachOpen && (
        <div className="absolute bottom-full left-2 right-2 mb-1 bg-surface border border-hairline rounded-lg shadow-xl z-10 overflow-hidden">
          <div className="px-2 py-1.5 border-b border-hairline">
            <input
              autoFocus
              value={attachQuery}
              onChange={(e) => searchAttach(e.target.value)}
              placeholder="Search customers, VPS, payments..."
              className="w-full bg-transparent text-base sm:text-xs text-ink-900 placeholder:text-ink-400 outline-none"
              onKeyDown={(e) => { if (e.key === 'Escape') setAttachOpen(false) }}
            />
          </div>
          <div className="max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
            {attachLoading && (
              <div className="px-3 py-2 text-[10px] text-ink-400">Searching...</div>
            )}
            {!attachLoading && attachQuery.length >= 2 && attachResults.length === 0 && (
              <div className="px-3 py-3 text-[10px] text-ink-400 text-center">No results</div>
            )}
            {attachResults.map((r) => (
              <button
                key={`${r.kind}:${r.id}`}
                onMouseDown={(e) => { e.preventDefault(); attachEntity(r) }}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-surface-alt cursor-pointer text-left"
              >
                <Icon name={iconForKind(r.kind)} variant="solid" className={cn('text-[10px] w-4 text-center', kindColor(r.kind))} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink-900 truncate">{r.display}</div>
                  {r.subtitle && <div className="text-[10px] text-ink-400 truncate">{r.subtitle}</div>}
                </div>
                <span className="text-[9px] text-ink-400 uppercase">{r.kind}</span>
              </button>
            ))}
            {attachQuery.length < 2 && (
              <div className="px-3 py-3 text-[10px] text-ink-400 text-center">
                Type to search customers, VPS, payments...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attached entity chips */}
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {mentions.map((m, i) => (
            <span
              key={`${m.kind}:${m.id}:${i}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-[10px] text-primary"
            >
              <Icon name={iconForKind(m.kind)} variant="solid" className="text-[9px]" />
              {m.display}
              <button
                onClick={() => setMentions(prev => prev.filter((_, j) => j !== i))}
                className="text-ink-400 hover:text-danger cursor-pointer ml-0.5"
              >
                <Icon name="xmark" variant="solid" className="text-[8px]" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-end gap-1 bg-surface-alt rounded-lg px-2 py-1.5">
        <button
          onClick={() => setAttachOpen(!attachOpen)}
          className={cn(
            'shrink-0 w-10 h-10 sm:w-7 sm:h-7 rounded-md flex items-center justify-center transition-colors cursor-pointer',
            attachOpen ? 'text-primary' : 'text-ink-400 hover:text-ink-700',
          )}
          title="Attach customer, VPS, payment..."
        >
          <Icon name="paperclip" variant="solid" className="text-[11px]" />
        </button>
        <textarea
          ref={ref}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="Type @ to mention..."
          rows={1}
          className="flex-1 bg-transparent text-base sm:text-xs text-ink-900 placeholder:text-ink-400 resize-none outline-none max-h-20 leading-relaxed"
          style={{ scrollbarWidth: 'none' }}
        />
        <button
          onClick={submit}
          disabled={(!text.trim() && mentions.length === 0) || sending}
          className={cn(
            'shrink-0 w-10 h-10 sm:w-7 sm:h-7 rounded-md flex items-center justify-center transition-colors',
            text.trim() || mentions.length > 0
              ? 'bg-primary text-white hover:bg-primary-hover cursor-pointer'
              : 'text-ink-400 cursor-default',
          )}
        >
          <Icon name="paper-plane" variant="solid" className="text-[11px]" />
        </button>
      </div>
    </div>
  )
}
